import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  Input,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  AccessTime as AccessTimeIcon,
  Close as CloseIcon
} from "@material-ui/icons";
import ConfirmButton from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import useNotifier from "@saleor/hooks/useNotifier";
import { buttonMessages } from "@saleor/intl";
import { useTableStyles } from "@saleor/remittanceRunner/styles/tableStyles";
import { RemittanceMallsConfig_malls_edges_node } from "@saleor/riderRemittanceConfig/types/RemittanceMallsConfig";
import { DialogProps } from "@saleor/types";
import {
  RemittanceConfigUpdateInput,
  RemittanceNotifTimeEnum
} from "@saleor/types/globalTypes";
import moment from "moment";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ReminderListTemplateSwitch } from "../ReminderList/ReminderListTemplateSwitch";
import { timeOptions } from "./TimeOptions";

interface ReminderDialogFormData {
  mallId: string;
  activateReminder: boolean;
  dailyRemittanceNotifTimes: string[];
  enable: boolean;
  codLimit: string;
  minRemittanceLimit: string;
}
interface ReminderInputData {
  mallId: string;
  input: RemittanceConfigUpdateInput;
}

const useStyles = makeStyles(
  _ => ({
    colView: {
      width: "48%"
    },
    customDisabledTextField: {
      "& > div": {
        backgroundColor: "#eff5f8"
      },
      pointerEvents: "none"
    },
    formControl: {
      border: "1px solid #BDBDBD;",
      borderRadius: "4px",
      overflow: "auto",
      padding: "2px 10px",
      width: "95%"
    },
    formControlLabel: {
      "& > span": {
        fontSize: "0.90rem",
        margin: "0"
      }
    },
    fullWidth: {
      width: "100%"
    },
    header: {
      alignItems: "center !important"
    },
    hideIcon: {
      position: "absolute",
      right: "30px",
      zIndex: 10
    },
    hideIconWithDescription: {
      marginBottom: "19px",
      position: "absolute",
      right: "30px",
      zIndex: 10
    },
    modalCloseBtn: {
      position: "absolute",
      right: "20px"
    },
    rowView: {
      alignItems: "start",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between"
    }
  }),
  {
    name: "ReminderDialog"
  }
);

export interface ReminderDialogProps extends DialogProps {
  onSubmit: (data: ReminderInputData) => void;
  mall: RemittanceMallsConfig_malls_edges_node;
}

const initialForm: ReminderDialogFormData = {
  activateReminder: true,
  codLimit: "0",
  dailyRemittanceNotifTimes: [],
  enable: false,
  mallId: "",
  minRemittanceLimit: "0"
};
const inputForm: ReminderInputData = {
  input: {
    activateReminder: true,
    codLimit: "0",
    dailyRemittanceNotifTimes: [],
    enable: false,
    minRemittanceLimit: "0"
  },
  mallId: ""
};

const PesoSign = () => (
  <Typography variant="body1" component="span" style={{ marginTop: "13px" }}>
    ₱
  </Typography>
);

const ReminderDialog: React.FC<ReminderDialogProps> = props => {
  const { open, onClose, onSubmit, mall } = props;

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 0;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 5.5 + ITEM_PADDING_TOP,
        width: 300
      }
    }
  };

  const intl = useIntl();
  const classes = useStyles(props);
  const tblClasses = useTableStyles(props);
  const notify = useNotifier();
  const [reminderTime, setReminderTime] = React.useState<string[]>([]);
  const [remittanceLimit, setRemittanceLimit] = React.useState("0");
  const [minRemittanceAmt, setMinRemittanceAmt] = React.useState("0");
  const [noRemittanceLimit, setNoRemittanceLimit] = React.useState(false);
  const [remittanceEnable, setRemittanceEnable] = React.useState(false);
  const [formIsUpdated, setFormIsUpdated] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setMallValues();
    } else {
      setReminderTime([]);
      setRemittanceLimit("0");
      setMinRemittanceAmt("0");
      setNoRemittanceLimit(false);
      setRemittanceEnable(false);
      setFormIsUpdated(false);
    }
  }, [open]);

  const handleReminderTimeChange = event => {
    const {
      target: { value }
    } = event;
    setReminderTime(typeof value === "string" ? value.split(",") : value);
  };

  const handleRemittanceLimitChange = event => {
    const {
      target: { value }
    } = event;
    setRemittanceLimit(value);
  };

  const handleMinRemittanceAmtChange = event => {
    const {
      target: { value }
    } = event;
    setMinRemittanceAmt(value);
  };

  const getRemittanceNotif = () => {
    const filtered = timeOptions.filter(to => reminderTime.includes(to.label));
    const array_labels: string[] = [];
    filtered.forEach(item => {
      array_labels.push(item.value);
    });
    const remittanceNotifArray: RemittanceNotifTimeEnum[] = array_labels.map(
      str => str as RemittanceNotifTimeEnum
    );
    return remittanceNotifArray;
  };

  const handleSubmit = () => {
    if (
      (noRemittanceLimit === false && minRemittanceAmt === "0") ||
      reminderTime.length === 0 ||
      remittanceLimit === "" ||
      minRemittanceAmt === ""
    ) {
      notify({
        status: "error",
        text: "Please complete all the required fields."
      });
    } else {
      inputForm.mallId = initialForm.mallId;
      inputForm.input.dailyRemittanceNotifTimes = getRemittanceNotif();
      inputForm.input.activateReminder = initialForm.activateReminder;
      inputForm.input.codLimit = remittanceLimit;
      inputForm.input.minRemittanceLimit = minRemittanceAmt;
      inputForm.input.enable = remittanceEnable;

      onSubmit(inputForm);
    }
  };

  const setMallValues = () => {
    if (mall?.remittanceConfig) {
      initialForm.mallId = mall.id;
      initialForm.dailyRemittanceNotifTimes =
        mall?.remittanceConfig?.dailyNotifTimes;
      initialForm.activateReminder = mall?.remittanceConfig?.reminderIsActive;
      initialForm.codLimit = mall?.remittanceConfig?.codLimit.toString();
      const mallMinRemittanceLimit =
        mall?.remittanceConfig?.minRemittanceLimit || 0;
      initialForm.minRemittanceLimit = parseFloat(
        mallMinRemittanceLimit.toString()
      )
        .toFixed(2)
        .replace(".00", "");
      initialForm.enable = mall?.remittanceConfig?.isEnable;

      setReminderTime(initialForm.dailyRemittanceNotifTimes);
      setRemittanceLimit(initialForm.codLimit);
      setMinRemittanceAmt(initialForm.minRemittanceLimit);
      setNoRemittanceLimit(initialForm.minRemittanceLimit === "0");
      setRemittanceEnable(initialForm.enable);
    }
  };

  React.useEffect(() => {
    setMallValues();
  }, [mall]);

  React.useEffect(() => {
    if (noRemittanceLimit) {
      setMinRemittanceAmt("0");
    }
  }, [noRemittanceLimit]);

  React.useEffect(() => {
    if (
      (noRemittanceLimit === false && minRemittanceAmt === "0") ||
      reminderTime.length === 0 ||
      remittanceLimit === "" ||
      minRemittanceAmt === ""
    ) {
      setFormIsUpdated(false);
    } else if (
      remittanceLimit !== initialForm.codLimit ||
      minRemittanceAmt !== initialForm.minRemittanceLimit ||
      reminderTime.toString() !==
        initialForm.dailyRemittanceNotifTimes.toString() ||
      remittanceEnable !== initialForm.enable
    ) {
      setFormIsUpdated(true);
    } else {
      setFormIsUpdated(false);
    }
  }, [remittanceLimit, minRemittanceAmt, reminderTime, remittanceEnable]);

  const formatTimeString = timeString => {
    const [hrs, min, sec] = timeString.split(":");
    const formattedTime = moment(new Date(0, 0, 1, hrs, min, sec)).format(
      "hh:mm A"
    );
    return formattedTime;
  };

  const displayReminderTime = (selected: any) => {
    const filtered = timeOptions.filter(to => selected.includes(to.label));
    const array_labels: string[] = [];

    filtered.forEach(item => {
      array_labels.push(formatTimeString(item.label));
    });

    return array_labels.join(", ");
  };

  const formatDate = _date =>
    moment
      .utc(_date)
      .local()
      .format("MMMM DD, YYYY, hh:mmA");

  return (
    <Dialog
      onClose={onClose}
      open={open}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: {
          borderRadius: "15px"
        }
      }}
    >
      <DialogTitle>
        <span style={{ fontWeight: 600 }}>Edit Remittance</span>
        <IconButton
          aria-label="close"
          onClick={() => onClose()}
          className={classes.modalCloseBtn}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogTitle>
        <div className={`${classes.header} ${classes.rowView}`}>
          <div style={{ width: "70%" }}>
            <p style={{ fontWeight: 500, margin: 0 }}>Enable Remittance</p>
            <p style={{ color: "#747C8C", fontSize: "small", margin: 0 }}>
              When enabled, remittance will automatically be accounted for in
              all future orders assigned to the rider.
            </p>
          </div>
          <ReminderListTemplateSwitch
            checked={remittanceEnable}
            onClick={() => setRemittanceEnable(!remittanceEnable)}
          />
        </div>
      </DialogTitle>
      <Form initial={initialForm} onSubmit={handleSubmit}>
        {({ submit }) => (
          <>
            <DialogContent>
              <div className={classes.rowView}>
                <TextField
                  fullWidth
                  label={intl.formatMessage({
                    defaultMessage: "Mall",
                    description: "input label"
                  })}
                  value={mall?.name}
                  name="mallId"
                  className={classes.customDisabledTextField}
                />
              </div>
              <FormSpacer />

              <div className={classes.rowView}>
                <div className={classes.fullWidth}>
                  <FormControl variant="outlined" className={classes.fullWidth}>
                    <div
                      className={`${classes.formControl} ${classes.fullWidth}`}
                    >
                      <InputLabel id="timeMultipleCheckboxLabel">
                        Reminder Time
                      </InputLabel>
                      <Select
                        multiple
                        id="timeMultipleCheckbox"
                        labelId="timeMultipleCheckboxLabel"
                        value={reminderTime}
                        input={<Input />}
                        renderValue={displayReminderTime}
                        MenuProps={MenuProps}
                        onChange={handleReminderTimeChange}
                        className={classes.fullWidth}
                        IconComponent={() => <AccessTimeIcon />}
                        disableUnderline={true}
                      >
                        {timeOptions.map(option => (
                          <MenuItem key={option.label} value={option.label}>
                            <Checkbox
                              checked={reminderTime.indexOf(option.label) > -1}
                            />
                            <ListItemText primary={option.timeLabel} />
                          </MenuItem>
                        ))}
                      </Select>
                    </div>
                  </FormControl>
                </div>
              </div>
              <FormSpacer />

              <div className={classes.rowView}>
                <div className={classes.colView}>
                  <TextField
                    className={classes.fullWidth}
                    label={intl.formatMessage({
                      defaultMessage: "Daily Remittance Limit",
                      description: "input label"
                    })}
                    value={remittanceLimit}
                    type="number"
                    inputProps={{
                      min: "1",
                      step: "1"
                    }}
                    InputProps={{
                      startAdornment: <PesoSign />
                    }}
                    onChange={event => {
                      if (event.target.value.length > 9) {
                        return;
                      }
                      handleRemittanceLimitChange(event);
                    }}
                  />
                </div>

                <div className={classes.colView}>
                  <TextField
                    className={
                      noRemittanceLimit
                        ? `${classes.fullWidth} ${classes.customDisabledTextField}`
                        : `${classes.fullWidth}`
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Min. Remittance Amount",
                      description: "input label"
                    })}
                    value={minRemittanceAmt}
                    type="number"
                    inputProps={
                      noRemittanceLimit
                        ? {
                            step: "1"
                          }
                        : {
                            min: "1",
                            step: "0.01"
                          }
                    }
                    InputProps={{
                      startAdornment: <PesoSign />
                    }}
                    onChange={event => {
                      if (event.target.value.length > 9) {
                        return;
                      }
                      handleMinRemittanceAmtChange(event);
                    }}
                  />

                  <FormControlLabel
                    className={classes.formControlLabel}
                    control={
                      <Checkbox
                        checked={noRemittanceLimit}
                        onChange={e => {
                          setNoRemittanceLimit(e.target.checked);
                        }}
                        color="primary"
                      />
                    }
                    label="No minimum remittance amount"
                  />
                </div>
              </div>
              <FormSpacer />

              <div className={classes.rowView}>
                <div
                  className={classes.fullWidth}
                  style={{ textAlign: "right" }}
                >
                  <Button onClick={onClose}>
                    <FormattedMessage {...buttonMessages.cancel} />
                  </Button>
                  <ConfirmButton
                    transitionState="default"
                    variant="contained"
                    color="secondary"
                    className={tblClasses.footerBtn}
                    type="submit"
                    onClick={() => () => submit()}
                    disabled={!formIsUpdated}
                  >
                    <FormattedMessage {...buttonMessages.saveChanges} />
                  </ConfirmButton>
                </div>
              </div>
            </DialogContent>
            <DialogActions style={{ justifyContent: "center" }}>
              First enabled on {formatDate(mall?.remittanceConfig?.enabledAt)}
            </DialogActions>
          </>
        )}
      </Form>
    </Dialog>
  );
};

ReminderDialog.displayName = "ReminderDialog";
export default ReminderDialog;
